import { useCallback, useEffect, useState } from "react";

/**
 * Helskärmsläge för kiosken.
 *
 * Safari har fortfarande bara de webkit-prefixade varianterna, så båda finns
 * med. iPhone saknar API:et helt — då är `supported` false och knappen ska inte
 * visas alls hellre än att sitta där och inte göra något.
 */

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

function currentElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

export function useFullscreen() {
  const [active, setActive] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const root = document.documentElement as FullscreenElement;
    setSupported(Boolean(root.requestFullscreen ?? root.webkitRequestFullscreen));

    // Läget kan ändras utan att knappen använts, t.ex. med F11 eller Escape.
    const sync = () => setActive(Boolean(currentElement()));
    sync();

    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggle = useCallback(async () => {
    const doc = document as FullscreenDocument;
    const root = document.documentElement as FullscreenElement;

    try {
      if (currentElement()) {
        await (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      } else {
        await (root.requestFullscreen?.() ?? root.webkitRequestFullscreen?.());
      }
    } catch {
      // Webbläsaren kan neka, t.ex. om anropet inte kommer från en klickning.
      // Inget att felhantera — läget står kvar som det var.
    }
  }, []);

  return { active, supported, toggle };
}
